import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import '../styles/RetrievalPathGraph.css';

/**
 * Component for visualizing the retrieval path and relationships between document chunks
 * that were used to generate an answer.
 */
const RetrievalPathGraph = ({ retrievalData, theme, onClose, onNodeSelect }) => {
  const svgRef = useRef(null);
  
  useEffect(() => {
    if (!retrievalData || !retrievalData.nodes || !retrievalData.links) return;
    
    // Clear previous graph
    d3.select(svgRef.current).selectAll('*').remove();
    
    const width = svgRef.current.clientWidth || 800;
    const height = svgRef.current.clientHeight || 400;
    
    // Create the SVG container
    const svg = d3.select(svgRef.current)
      .attr('width', "100%")
      .attr('height', "100%")
      .attr('viewBox', [0, 0, width, height])
      .attr('class', `retrieval-graph ${theme}`);
    
    // Create a group for the graph elements
    const g = svg.append('g')
      .attr('class', 'graph-container');
    
    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    
    svg.call(zoom);
    
    // Create the simulation for the force-directed graph
    const simulation = d3.forceSimulation(retrievalData.nodes)
      .force('link', d3.forceLink(retrievalData.links).id(d => d.id).distance(150))
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(60));
    
    // Create the links
    const links = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(retrievalData.links)
      .enter()
      .append('line')
      .attr('class', d => `link ${d.type || 'default'}`)
      .attr('stroke-width', d => d.weight ? 1 + (d.weight * 2) : 1);
    
    // Add arrowheads for the links
    svg.append('defs').selectAll('marker')
      .data(['retrieval', 'contribution', 'default'])
      .enter().append('marker')
      .attr('id', d => `arrow-${d}`)
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('class', d => `arrow-${d}`);
    
    links.attr('marker-end', d => `url(#arrow-${d.type || 'default'})`);
    
    // Create the nodes
    const nodeGroups = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(retrievalData.nodes)
      .enter()
      .append('g')
      .attr('class', d => `node-group ${d.type}`)
      .on('click', (event, d) => {
        event.stopPropagation();
        // Call onNodeSelect if provided
        if (onNodeSelect) {
          onNodeSelect(d);
        }
      })
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));
    
    // Add circle for each node
    nodeGroups.append('circle')      
    .attr('r', d => {
        // Size based on node type
        if (d.type === 'question') return 25;
        if (d.type === 'answer') return 30;
        // For document nodes, use a fixed size (removed relevance score sizing)
        return 20;
      })
      .attr('class', d => `node ${d.type}`)
      .attr('fill', d => {
        if (d.type === 'question') return '#5b9bd5';
        if (d.type === 'answer') return '#70ad47';
        if (d.type === 'document') return '#ed7d31';
        return '#999';
      });
    
    // Add text labels
    nodeGroups.append('text')
      .attr('dy', 4)
      .attr('text-anchor', 'middle')
      .attr('class', 'node-label')
      .text(d => {
        if (d.type === 'question') return 'Q';
        if (d.type === 'answer') return 'A';
        if (d.type === 'document') {
          // For document nodes, show the index
          return `D${d.id.replace('doc_', '')}`;
        }
        return d.id?.substring(0, 3) || '?';
      });
    
    // Add metadata for document nodes (page number)
    nodeGroups.filter(d => d.type === 'document' && d.page)
      .append('text')
      .attr('dy', -20)
      .attr('text-anchor', 'middle')
      .attr('class', 'metadata-label')      .text(d => `p.${d.page || '?'}`);
    
    // Removed relevance score indicators as requested
    
    // Add tooltips
    nodeGroups.append('title')
      .text(d => {
        if (d.type === 'question') return `Question: ${d.content?.substring(0, 100) || 'User Question'}...`;
        if (d.type === 'answer') return `Answer: ${d.content?.substring(0, 100) || 'AI Response'}...`;        if (d.type === 'document') {
          let tooltip = `Document: ${d.filename || d.id}\n`;
          tooltip += `Page: ${d.page || 'N/A'}\n`;
          // Removed relevance score display
          tooltip += `\n${d.content?.substring(0, 150) || 'No content available'}...`;
          return tooltip;
        }
        return d.id || 'Unknown Node';
      });
    
    // Add legend
    const legend = svg.append('g')
      .attr('class', 'legend')
      .attr('transform', 'translate(20, 20)');
    
    const legendData = [
      { type: 'question', label: 'User Question', color: '#5b9bd5' },
      { type: 'answer', label: 'AI Answer', color: '#70ad47' },
      { type: 'document', label: 'Document Chunk', color: '#ed7d31' },
      { type: 'retrieval', label: 'Retrieval Path', color: '#5b9bd5', isLink: true },
      { type: 'contribution', label: 'Contribution to Answer', color: '#70ad47', isLink: true }
    ];
    
    const legendItems = legend.selectAll('.legend-item')
      .data(legendData)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(0, ${i * 20})`);
    
    legendItems.append(d => d.isLink ? document.createElementNS('http://www.w3.org/2000/svg', 'line') : document.createElementNS('http://www.w3.org/2000/svg', 'circle'))
      .attr('r', d => d.isLink ? null : 6)
      .attr('x1', d => d.isLink ? 0 : null)
      .attr('y1', d => d.isLink ? 0 : null)
      .attr('x2', d => d.isLink ? 12 : null)
      .attr('y2', d => d.isLink ? 0 : null)
      .attr('stroke', d => d.isLink ? d.color : null)
      .attr('stroke-width', d => d.isLink ? 2 : null)
      .attr('fill', d => d.isLink ? 'none' : d.color)
      .attr('class', d => d.isLink ? `link ${d.type}` : null);
    
    legendItems.append('text')
      .attr('x', 20)
      .attr('y', 4)
      .text(d => d.label)
      .attr('class', 'legend-text');
    
    // Update positions on simulation tick
    simulation.on('tick', () => {
      links
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);
      
      nodeGroups.attr('transform', d => `translate(${d.x}, ${d.y})`);
    });
    
    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }
    
    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }
    
    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }
    
    // Return a cleanup function
    return () => {
      simulation.stop();
    };
  }, [retrievalData, theme, onNodeSelect]);
  
  if (!retrievalData || !retrievalData.nodes || retrievalData.nodes.length === 0) {
    return <div className="retrieval-path-empty">No retrieval path data available</div>;
  }
  
  return (
    <div className="retrieval-path-graph-container">
      <svg ref={svgRef} className={`retrieval-path-graph ${theme}`}></svg>
    </div>
  );
};

export default RetrievalPathGraph;